import React, { useState } from 'react';
import { 
  Calendar, 
  CreditCard, 
  History, 
  Trophy, 
  Activity,
  Settings,
  Bell,
  Mail,
  Phone,
  Search,
  Filter,
  X,
  CheckCircle,
  XCircle,
  Clock,
  Plus,
  Download,
  Euro,
  Wallet,
  Smartphone,
  CreditCard as CardIcon,
  PlusCircle,
  Trash2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Building
} from 'lucide-react';

type PaymentMethod = {
  id: string;
  type: 'mvola' | 'orange-money' | 'card' | 'club';
  label: string;
  last4?: string;
  expiryDate?: string;
  phoneNumber?: string;
  isDefault: boolean;
};

type Transaction = {
  id: string;
  date: string;
  type: 'credit' | 'debit';
  description: string;
  amount: number;
  status: 'completed' | 'pending' | 'failed';
  paymentMethod: PaymentMethod;
};

type Reservation = {
  id: string;
  courtId: string;
  courtName: string;
  courtType: string;
  date: string;
  startTime: string;
  duration: number;
  price: number;
  status: 'completed' | 'upcoming' | 'cancelled';
  players?: string[];
  notes?: string;
};

function Members() {
  const [activeTab, setActiveTab] = useState<'overview' | 'reservations' | 'billing'>('overview');
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState<'mvola' | 'orange-money' | 'card' | 'club' | null>(null);
  const [newPaymentDetails, setNewPaymentDetails] = useState({
    phoneNumber: '',
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  });

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([
    {
      id: '1',
      type: 'mvola',
      label: 'MVola',
      phoneNumber: '+261 34 12 345 67',
      isDefault: true
    },
    {
      id: '2',
      type: 'orange-money',
      label: 'Orange Money',
      phoneNumber: '+261 32 11 223 44',
      isDefault: false
    },
    {
      id: '3',
      type: 'card',
      label: 'Visa',
      last4: '4242',
      expiryDate: '12/25',
      isDefault: false
    },
    {
      id: '4',
      type: 'club',
      label: 'Paiement au club',
      isDefault: false
    }
  ]);

  const [transactions] = useState<Transaction[]>([
    {
      id: '1',
      date: '2024-03-18',
      type: 'debit',
      description: 'Réservation Terrain 1',
      amount: 25,
      status: 'completed',
      paymentMethod: paymentMethods[0]
    },
    {
      id: '2',
      date: '2024-03-15',
      type: 'credit',
      description: 'Recharge compte',
      amount: 100,
      status: 'completed',
      paymentMethod: paymentMethods[2]
    }
  ]);

  const [reservations] = useState<Reservation[]>([
    {
      id: '1',
      courtId: 'court1',
      courtName: 'Terrain 1',
      courtType: 'Couvert',
      date: '2024-03-20',
      startTime: '10:30',
      duration: 90,
      price: 25,
      status: 'upcoming',
      players: ['John Doe', 'Jane Smith']
    },
    {
      id: '2',
      courtId: 'court2',
      courtName: 'Terrain 2',
      courtType: 'Extérieur',
      date: '2024-03-22',
      startTime: '15:00',
      duration: 90,
      price: 25,
      status: 'upcoming'
    },
    {
      id: '3',
      courtId: 'court1',
      courtName: 'Terrain 1',
      courtType: 'Couvert',
      date: '2024-03-15',
      startTime: '14:00',
      duration: 90,
      price: 25,
      status: 'completed',
      players: ['John Doe', 'Mike Johnson']
    },
    {
      id: '4',
      courtId: 'court2',
      courtName: 'Terrain 2',
      courtType: 'Extérieur',
      date: '2024-03-10',
      startTime: '11:30',
      duration: 90,
      price: 25,
      status: 'cancelled'
    }
  ]);

  const handleAddPayment = () => {
    if (!selectedPaymentType) return;

    let newPayment: PaymentMethod;

    switch (selectedPaymentType) {
      case 'mvola':
      case 'orange-money':
        if (!newPaymentDetails.phoneNumber) {
          alert('Veuillez entrer un numéro de téléphone');
          return;
        }
        newPayment = {
          id: Date.now().toString(),
          type: selectedPaymentType,
          label: selectedPaymentType === 'mvola' ? 'MVola' : 'Orange Money',
          phoneNumber: newPaymentDetails.phoneNumber,
          isDefault: paymentMethods.length === 0
        };
        break;

      case 'card':
        if (!newPaymentDetails.cardNumber || !newPaymentDetails.expiryDate || !newPaymentDetails.cvv) {
          alert('Veuillez remplir tous les champs');
          return;
        }
        newPayment = {
          id: Date.now().toString(),
          type: 'card',
          label: 'Carte bancaire',
          last4: newPaymentDetails.cardNumber.slice(-4),
          expiryDate: newPaymentDetails.expiryDate,
          isDefault: paymentMethods.length === 0
        };
        break;

      case 'club':
        newPayment = {
          id: Date.now().toString(),
          type: 'club',
          label: 'Paiement au club',
          isDefault: paymentMethods.length === 0
        };
        break;

      default:
        return;
    }

    setPaymentMethods([...paymentMethods, newPayment]);
    setShowAddPayment(false);
    setSelectedPaymentType(null);
    setNewPaymentDetails({
      phoneNumber: '',
      cardNumber: '',
      expiryDate: '',
      cvv: ''
    });
  };

  const setDefaultPayment = (id: string) => {
    setPaymentMethods(methods =>
      methods.map(method => ({
        ...method,
        isDefault: method.id === id
      }))
    );
  };

  const deletePayment = (id: string) => {
    const methodToDelete = paymentMethods.find(m => m.id === id);
    if (methodToDelete?.isDefault && paymentMethods.length > 1) {
      alert('Veuillez définir un autre moyen de paiement par défaut avant de supprimer celui-ci');
      return;
    }
    
    setPaymentMethods(methods => {
      const remaining = methods.filter(m => m.id !== id);
      if (methodToDelete?.isDefault && remaining.length > 0) {
        remaining[0].isDefault = true;
      }
      return remaining;
    });
  };

  const renderPaymentMethodIcon = (type: PaymentMethod['type']) => {
    switch (type) {
      case 'mvola':
        return <Wallet className="h-6 w-6 text-purple-600" />;
      case 'orange-money':
        return <Smartphone className="h-6 w-6 text-orange-600" />;
      case 'card':
        return <CardIcon className="h-6 w-6 text-blue-600" />;
      case 'club':
        return <Building className="h-6 w-6 text-green-600" />;
    }
  };

  const renderBillingContent = () => {
    return (
      <div className="space-y-8">
        {/* Payment Methods Section */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Moyens de paiement</h3>
            <button
              onClick={() => setShowAddPayment(true)}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
            >
              <PlusCircle className="h-5 w-5" />
              <span>Ajouter</span>
            </button>
          </div>

          <div className="space-y-4">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-4">
                  {renderPaymentMethodIcon(method.type)}
                  <div>
                    <p className="font-semibold">{method.label}</p>
                    <p className="text-sm text-gray-600">
                      {method.type === 'card'
                        ? `**** **** **** ${method.last4}`
                        : method.type === 'club'
                        ? 'Paiement sur place'
                        : method.phoneNumber}
                    </p>
                  </div>
                  {method.isDefault && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      Par défaut
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {!method.isDefault && (
                    <button
                      onClick={() => setDefaultPayment(method.id)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Définir par défaut
                    </button>
                  )}
                  <button
                    onClick={() => deletePayment(method.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add Payment Method Modal */}
        {showAddPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Ajouter un moyen de paiement</h3>
                <button
                  onClick={() => {
                    setShowAddPayment(false);
                    setSelectedPaymentType(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {!selectedPaymentType ? (
                <div className="space-y-4">
                  <button
                    onClick={() => setSelectedPaymentType('mvola')}
                    className="w-full flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <Wallet className="h-6 w-6 text-purple-600" />
                    <span>MVola</span>
                  </button>
                  <button
                    onClick={() => setSelectedPaymentType('orange-money')}
                    className="w-full flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <Smartphone className="h-6 w-6 text-orange-600" />
                    <span>Orange Money</span>
                  </button>
                  <button
                    onClick={() => setSelectedPaymentType('card')}
                    className="w-full flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <CardIcon className="h-6 w-6 text-blue-600" />
                    <span>Carte bancaire</span>
                  </button>
                  <button
                    onClick={() => setSelectedPaymentType('club')}
                    className="w-full flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <Building className="h-6 w-6 text-green-600" />
                    <span>Paiement au club</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {(selectedPaymentType === 'mvola' || selectedPaymentType === 'orange-money') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Numéro de téléphone
                      </label>
                      <input
                        type="tel"
                        value={newPaymentDetails.phoneNumber}
                        onChange={(e) => setNewPaymentDetails({
                          ...newPaymentDetails,
                          phoneNumber: e.target.value
                        })}
                        placeholder="+261 XX XX XXX XX"
                        className="w-full p-2 border rounded-md"
                      />
                    </div>
                  )}

                  {selectedPaymentType === 'card' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Numéro de carte
                        </label>
                        <input
                          type="text"
                          value={newPaymentDetails.cardNumber}
                          onChange={(e) => setNewPaymentDetails({
                            ...newPaymentDetails,
                            cardNumber: e.target.value
                          })}
                          placeholder="1234 5678 9012 3456"
                          className="w-full p-2 border rounded-md"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date d'expiration
                          </label>
                          <input
                            type="text"
                            value={newPaymentDetails.expiryDate}
                            onChange={(e) => setNewPaymentDetails({
                              ...newPaymentDetails,
                              expiryDate: e.target.value
                            })}
                            placeholder="MM/YY"
                            className="w-full p-2 border rounded-md"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            CVV
                          </label>
                          <input
                            type="text"
                            value={newPaymentDetails.cvv}
                            onChange={(e) => setNewPaymentDetails({
                              ...newPaymentDetails,
                              cvv: e.target.value
                            })}
                            placeholder="123"
                            className="w-full p-2 border rounded-md"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {selectedPaymentType === 'club' && (
                    <div className="text-gray-600">
                      <p>En choisissant cette option, vous pourrez payer directement au club lors de votre venue.</p>
                      <p className="mt-2">Note : Certaines fonctionnalités peuvent être limitées avec ce mode de paiement.</p>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={() => {
                        setSelectedPaymentType(null);
                        setNewPaymentDetails({
                          phoneNumber: '',
                          cardNumber: '',
                          expiryDate: '',
                          cvv: ''
                        });
                      }}
                      className="px-4 py-2 border rounded-md hover:bg-gray-50"
                    >
                      Retour
                    </button>
                    <button
                      onClick={handleAddPayment}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Ajouter
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transactions Section */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Historique des transactions</h3>
          <div className="space-y-4">
            {transactions.map((transaction) => (
              <div
                key={transaction.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-4">
                  {renderPaymentMethodIcon(transaction.paymentMethod.type)}
                  <div>
                    <p className="font-semibold">{transaction.description}</p>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <span>{new Date(transaction.date).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>{transaction.paymentMethod.label}</span>
                      {transaction.paymentMethod.type === 'card' && (
                        <>
                          <span>•</span>
                          <span>**** {transaction.paymentMethod.last4}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <span className={`font-semibold ${
                  transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {transaction.type === 'credit' ? '+' : '-'}{transaction.amount} €
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderReservationStatus = (status: string) => {
    switch (status) {
      case 'upcoming':
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
            À venir
          </span>
        );
      case 'completed':
        return (
          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
            Terminé
          </span>
        );
      case 'cancelled':
        return (
          <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">
            Annulé
          </span>
        );
      default:
        return null;
    }
  };

  const renderReservationsContent = () => {
    const upcomingReservations = reservations.filter(r => r.status === 'upcoming');
    const pastReservations = reservations.filter(r => r.status !== 'upcoming');

    return (
      <div className="space-y-8">
        {/* Filtres et recherche */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="relative">
            <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une réservation..."
              className="pl-10 pr-4 py-2 border rounded-md w-full sm:w-64 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center space-x-4">
            <select className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="all">Tous les terrains</option>
              <option value="court1">Terrain 1</option>
              <option value="court2">Terrain 2</option>
            </select>
            <select className="border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="all">Tous les statuts</option>
              <option value="upcoming">À venir</option>
              <option value="completed">Terminé</option>
              <option value="cancelled">Annulé</option>
            </select>
          </div>
        </div>

        {/* Réservations à venir */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Réservations à venir</h3>
          <div className="space-y-4">
            {upcomingReservations.map(reservation => (
              <div
                key={reservation.id}
                className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="text-lg font-semibold">{reservation.courtName}</h4>
                      <span className="text-sm text-gray-500">({reservation.courtType})</span>
                    </div>
                    <div className="mt-2 space-y-1 text-gray-600">
                      <p>
                        {new Date(reservation.date).toLocaleDateString('fr-FR', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                      <p>{reservation.startTime} - {reservation.duration} minutes</p>
                      {reservation.players && (
                        <p className="text-sm">
                          Joueurs : {reservation.players.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    {renderReservationStatus(reservation.status)}
                    <p className="font-semibold">{reservation.price} €</p>
                    <button className="text-red-600 hover:text-red-800 text-sm">
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Historique des réservations */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Historique des réservations</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Terrain
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Horaire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prix
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pastReservations.map(reservation => (
                  <tr key={reservation.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {new Date(reservation.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium">{reservation.courtName}</div>
                        <div className="text-sm text-gray-500">{reservation.courtType}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {reservation.startTime}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {reservation.price} €
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderReservationStatus(reservation.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button className="text-blue-600 hover:text-blue-800">
                        Réserver à nouveau
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-gray-200 pt-4">
          <div className="flex items-center">
            <select className="border rounded-md px-2 py-1 text-sm">
              <option value="10">10 par page</option>
              <option value="20">20 par page</option>
              <option value="50">50 par page</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 border rounded-md hover:bg-gray-50">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="text-sm text-gray-700">
              Page 1 sur 1
            </span>
            <button className="p-2 border rounded-md hover:bg-gray-50">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Espace Membres</h1>
          <p className="text-gray-600">Gérez vos réservations et paiements</p>
        </div>
        <button className="relative p-2 text-gray-600 hover:text-blue-600">
          <Bell className="h-6 w-6" />
          <span className="absolute top-0 right-0 h-3 w-3 bg-red-500 rounded-full"></span>
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-8">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
            activeTab === 'overview' ? 'bg-white shadow' : 'hover:bg-gray-200'
          }`}
        >
          <Activity className="h-5 w-5" />
          <span>Vue d'ensemble</span>
        </button>
        <button
          onClick={() => setActiveTab('reservations')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
            activeTab === 'reservations' ? 'bg-white shadow' : 'hover:bg-gray-200'
          }`}
        >
          <Calendar className="h-5 w-5" />
          <span>Réservations</span>
        </button>
        <button
          onClick={() => setActiveTab('billing')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
            activeTab === 'billing' ? 'bg-white shadow' : 'hover:bg-gray-200'
          }`}
        >
          <CreditCard className="h-5 w-5" />
          <span>Paiements</span>
        </button>
      </div>

      {/* Main Content */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-gray-600">Crédit disponible</h3>
                <CreditCard className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold">75,00 €</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-gray-600">Réservations ce mois</h3>
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold">8</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-gray-600">Points fidélité</h3>
                <History className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold">450</p>
            
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-gray-600">Niveau</h3>
                <Trophy className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold">3.5</p>
            </div>
          </div>

          {/* Upcoming Reservations */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Prochaines réservations</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-semibold">Terrain 1</p>
                  <p className="text-gray-600">2024-03-20 à 10:30</p>
                </div>
                <button className="text-red-600 hover:text-red-800">
                  Annuler
                </button>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-semibold">Terrain 2</p>
                  <p className="text-gray-600">2024-03-22 à 15:00</p>
                </div>
                <button className="text-red-600 hover:text-red-800">
                  Annuler
                </button>
              </div>
            </div>
            <button className="mt-4 text-blue-600 hover:text-blue-800">
              Voir toutes les réservations →
            </button>
          </div>

          {/* Recent Transactions */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Transactions récentes</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-semibold">Réservation Terrain 1</p>
                  <p className="text-gray-600">2024-03-18</p>
                </div>
                <span className="font-semibold text-red-600">-25 €</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-semibold">Recharge compte</p>
                  <p className="text-gray-600">2024-03-15</p>
                </div>
                <span className="font-semibold text-green-600">+100 €</span>
              </div>
            </div>
            <button className="mt-4 text-blue-600 hover:text-blue-800">
              Voir toutes les transactions →
            </button>
          </div>
        </div>
      )}

      {activeTab === 'reservations' && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-6">Historique des réservations</h2>
          {renderReservationsContent()}
        </div>
      )}

      {activeTab === 'billing' && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-6">Gestion des paiements</h2>
          {renderBillingContent()}
        </div>
      )}
    </div>
  );
}

export default Members;